import axios from 'axios';
import { buildApiUrl } from '../config/api';

export const UsuarioService = {
    fetchUsuarios: async ({ token, pagina = 1, pageSize = 20, buscar = '', perfil = '', estado = '' }) => {
        try {
            const params = { pagina, pageSize, buscar, perfil, estado };

            const response = await axios.get(buildApiUrl('/api/users'), {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            return {
                usuarios: response.data.users || [],
                totalPaginas: response.data.totalPaginas || 1,
                totalRegistros: response.data.totalRegistros || 0,
            };
        } catch (error) {
            console.error('Error en UsuarioService.fetchUsuarios:', error);
            throw error;
        }
    },
    consultarUsuarioPorId: async ({ token, id }) => {
        try {
            const response = await axios.get(buildApiUrl(`/api/users/${id}`), {
                headers: { Authorization: `Bearer ${token}` },
            });

            return response.data;
        } catch (error) {
            console.error('Error en UsuarioService.consultarUsuarioPorId:', error);
            throw error;
        }
    },
    consultarDispositivoUsuarioPorId: async ({ token, id }) => {
        try {
            const response = await axios.get(buildApiUrl(`/api/users/${id}/dispositivos`), {
                headers: { Authorization: `Bearer ${token}` },
            });

            return response.data;
        } catch (error) {
            console.error('Error en UsuarioService.consultarUsuarioPorId:', error);
            throw error;
        }
    },
    desactivarDispositivoUsuario: async ({ token, id, idDispositivo }) => {
        try {
            await axios.post(buildApiUrl(`/api/users/${id}/desactivarDispositivo`), { idDispositivo }, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (error) {
            console.error('Error en UsuarioService.darDeBajaUsuario:', error);
            throw error;
        }
    },
    consultarPermisosPorUsuario: async ({ token, id }) => {
        try {
            const response = await axios.get(buildApiUrl(`/api/users/${id}/permisos`), {
                headers: { Authorization: `Bearer ${token}` },
            });

            return response.data.user?.permisos || []; // o response.data.permisos si simplificas la respuesta
        } catch (error) {
            console.error('Error en UsuarioService.consultarPermisosPorUsuario:', error);
            throw error;
        }
    },
    darDeBajaUsuario: async ({ token, id, bajaForm }) => {
        try {
            await axios.post(buildApiUrl(`/api/users/${id}/baja`), bajaForm, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (error) {
            console.error('Error en UsuarioService.darDeBajaUsuario:', error);
            throw error;
        }
    },
    darDeBajaUsuarioSolicitud: async ({ token, id, bajaForm }) => {
        try {
            await axios.post(buildApiUrl(`/api/users/${id}/bajasolicitud`), bajaForm, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (error) {
            console.error('Error en UsuarioService.darDeBajaUsuario:', error);
            throw error;
        }
    },
    bajaUsuarioSolicitud: async ({ token, id, bajaForm }) => {
        try {
            await axios.put(buildApiUrl(`/api/users/${id}/solicitud`), bajaForm, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (error) {
            console.error('Error en UsuarioService.darDeBajaUsuario:', error);
            throw error;
        }
    },
    exportarUsuariosExcel: async ({ token, filtroBusqueda = '', filtroPerfil = '' }) => {
        try {
            const response = await axios.post(
                buildApiUrl('/api/users/exportar'),
                { filtroBusqueda, filtroPerfil },
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob',
                }
            );

            return response.data; // Blob
        } catch (error) {
            console.error('Error en UsuarioService.exportarUsuariosExcel:', error);
            throw error;
        }
    },
    importarUsuarios: async ({ token, archivo }) => {
        try {
            const formData = new FormData();
            formData.append('archivo', archivo);

            const response = await axios.post(buildApiUrl('/api/users/importar'), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data; // { success, resultados }
        } catch (error) {
            console.error('Error en UsuarioService.importarUsuarios:', error);
            throw error;
        }
    },
    fetchJefes: async (token) => {
        try {
            const response = await axios.get(buildApiUrl('/api/users/jefes'), {
                headers: { Authorization: `Bearer ${token}` },
            });

            return response.data || [];
        } catch (error) {
            console.error('Error en UsuarioService.fetchJefes:', error);
            return [];
        }
    },
    fetchVendedores: async (token) => {
        try {
            const response = await axios.get(buildApiUrl('/api/users/vendedores'), {
                headers: { Authorization: `Bearer ${token}` },
            });

            return response.data || [];
        } catch (error) {
            console.error('Error en UsuarioService.fetchJefes:', error);
            return [];
        }
    },
    fetchSupervisores: async (token) => {
        try {
            const response = await axios.get(buildApiUrl('/api/users/supervisores'), {
                headers: { Authorization: `Bearer ${token}` },
            });

            return response.data || [];
        } catch (error) {
            console.error('Error en UsuarioService.fetchSupervisores:', error);
            return [];
        }
    },
    guardarUsuario: async ({ token, userData, isEdicion = false, idUsuario = null }) => {
        try {
            const url = isEdicion && idUsuario
                ? buildApiUrl(`/api/users/${idUsuario}`)
                : buildApiUrl('/api/users');

            const method = isEdicion ? 'put' : 'post';

            const response = await axios[method](url, userData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            return response.data;
        } catch (error) {
            console.error('Error en UsuarioService.guardarUsuario:', error);
            throw error;
        }
    },
    obtenerDatosPersonales: async (token) => {
        try {
            const response = await axios.get(buildApiUrl('/api/users/me'), {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data || null;
        } catch (error) {
            console.error('Error en UsuarioService.obtenerDatosPersonales:', error);
            throw error;
        }
    },
    actualizarDatosBasicos: async ({ token, userId, updateData }) => {
        try {
            const response = await axios.put(
                buildApiUrl(`/api/users/${userId}/datos-basicos`),
                updateData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error('Error en UsuarioService.actualizarDatosBasicos:', error);
            throw error;
        }
    },
    fetchBajas: async ({ token, pagina = 1, pageSize = 20, desde, hasta, buscar }) => {
        try {
            const params = { pagina, pageSize, buscar, desde, hasta };

            const response = await axios.get(buildApiUrl('/api/users/solicitudbaja'), {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            return {
                usuarios: response.data.users || [],
                totalPaginas: response.data.totalPaginas || 1,
                totalRegistros: response.data.totalRegistros || 0,
            };
        } catch (error) {
            console.error('Error en UsuarioService.fetchBajas:', error);
            throw error;
        }
    },
    fetchTeam: async ({ token, pagina = 1, pageSize = 20, documento = '', nombre = '', jefe = '', supervisor = '' }) => {
        try {
            const params = { pagina, pageSize, documento, nombre, jefe, supervisor };

            const response = await axios.get(buildApiUrl('/api/users/team'), {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            return {
                usuarios: response.data.users || [],
                totalPaginas: response.data.totalPaginas || 1,
                totalRegistros: response.data.totalRegistros || 0,
            };
        } catch (error) {
            console.error('Error en UsuarioService.fetchUsuarios:', error);
            throw error;
        }
    },
};
